import { BlackjackDecision, BlackjackResult } from '@/models/games/blackjack';
import { BetType } from '@/models/games/blackjack/enums/BetType';
import { HandResult } from '@/models/games/blackjack/GameResult';
import { GameType } from '@/models/games/enums/GameType';
import { BetRecord } from '@/modules/report/slice/betSummary';
import { Surrender } from '@/modules/rwdGame/blackjack/icon/Surrender';
import {
    IcoBJ,
    IcoBJBust,
    IcoBJLose,
    IcoBJPush,
    IcoBJWin,
} from '@/modules/setting/svg/oldBJ';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BJGameResultCellProps } from '..';
import { BetRecordPokerList } from '../../pokerList/BetRecordPokerList';
import { BetRecordScroller } from '../../scroller/BetRecordScroller';

enum IconEnum {
    BJ,
    Bush,
    Push,
    Win,
    Lose,
    Surrender,
}
const getPointText = (point: number): string => {
    let tmp = '';
    if (point == 255) {
        tmp = 'BJ';
    } else if (point == 22) {
        tmp = 'B';
    } else {
        tmp = point.toString();
    }
    return tmp;
};
const getSeatsHand = (
    seat: number,
    result: BlackjackResult,
    gameid: number
): Array<BJHand> => {
    let hands = [] as Array<BJHand>;
    const seatResult = result.P.find(v => v.SN == seat + 1);
    const resultHands = seatResult?.H;
    if (resultHands) {
        const isSurrender = seatResult.D == BlackjackDecision.Surrender;
        for (let i = 0; i < resultHands.length; i++) {
            const isBJ = resultHands[i].R == 255;
            const isB = resultHands[i].R > 21;
            const isPush =
                result.B.R == resultHands[i].R && resultHands[i].R !== 22;
            const isWin =
                (resultHands[i].R > result.B.R || result.B.R == 22) &&
                !isB &&
                !isSurrender;
            hands.push({
                key: `bj-record-hand-cell-${gameid}-${seat}-${i}`,
                name: resultHands.length > 1 ? `#${i + 1}` : '  ',
                cards: resultHands[i].PK,
                point: getPointText(resultHands[i].R),
                icon: isSurrender
                    ? IconEnum.Surrender
                    : isPush
                      ? IconEnum.Push
                      : isBJ
                        ? IconEnum.BJ
                        : isB
                          ? IconEnum.Bush
                          : isWin
                            ? IconEnum.Win
                            : IconEnum.Lose,
            });
        }
    }
    return hands;
};
const getBetSeats = (
    betRecords: Array<BetRecord> | undefined
): Array<number> => {
    let seats = [] as Array<number>;
    if (betRecords) {
        for (let i = 0; i < betRecords.length; i++) {
            if (betRecords[i].BetType != BetType.BJB_Insurance) {
                let tmp = betRecords[i].BetType % 7;
                if (seats.indexOf(tmp) < 0) {
                    seats.push(tmp);
                }
            }
        }
    }
    return seats;
};
type BJHand = {
    key: string;
    name: string;
    cards: Array<number>;
    point: string;
    icon: IconEnum;
};
type BJResult = {
    key: string;
    seat: string;
    hands: Array<BJHand>;
};
const seatName = ['P1', 'P2', 'P3', 'P5', 'P6', 'P7', 'P8'];
export const BlackjackGameResult = (props: BJGameResultCellProps) => {
    const { summary, betRecords, expanded } = props;
    const betSeats = useMemo(() => getBetSeats(betRecords), [betRecords]);
    const blackjackResult = useMemo(() => {
        let result = undefined;
        try {
            result = JSON.parse(summary.Result) as BlackjackResult;
        } catch (e) {
            console.error(e);
        }
        return result;
    }, [summary]);
    const [resultList, setResultList] = useState<Array<BJResult>>([]);
    useEffect(() => {
        if (blackjackResult) {
            let list = [] as Array<BJResult>;
            for (let i = 0; i < betSeats.length; i++) {
                list.push({
                    key: `bj-record-seat-cell-${summary.GameID}-${i}`,
                    seat: seatName[betSeats[i]],
                    hands: getSeatsHand(
                        betSeats[i],
                        blackjackResult,
                        summary.GameID
                    ),
                });
            }
            setResultList(list);
        }
    }, [betSeats, blackjackResult]);
    // TODO
    return (
        <div className="item-blackjack-game-result-container">
            <>
                {blackjackResult && (
                    <MainResult
                        dealerHand={blackjackResult.B}
                        betSeats={betSeats}
                    />
                )}
                {expanded &&
                    resultList.map((v, i) => {
                        return (
                            <SeatCell key={v.key} seat={v} isFirst={i == 0} />
                        );
                    })}
            </>
        </div>
    );
};
type MainProp = {
    dealerHand: HandResult;
    betSeats: Array<number>;
};
const MainResult = (props: MainProp) => {
    const { t } = useTranslation();
    const { dealerHand, betSeats } = props;
    const color = useMemo(() => {
        let color = [0, 0, 0, 0, 0, 0, 0];
        for (let i = 0; i < color.length; i++) {
            if (betSeats.indexOf(i) >= 0) {
                color[i] = 1;
            }
        }
        return color;
    }, [betSeats]);
    const point = useMemo(() => getPointText(dealerHand.R), [dealerHand.R]);

    return (
        <div className="blackjack-result-frame dealer">
            <div className="item-text-font">{t('tableinfo.dealer')}</div>
            <div className="blackjack-card-frame">
                <BetRecordPokerList
                    decks={[dealerHand.PK]}
                    gameType={GameType.Blackjack}
                />
                <div className="item-text-font white">{point}</div>
            </div>
            <div className="blackjack-dealer-result-svg">
                <SeatSvg color={color} />
            </div>
        </div>
    );
};

type SeatProp = {
    isFirst: boolean;
    seat: BJResult;
};
const SeatCell = (props: SeatProp) => {
    const { seat } = props;
    return (
        <div className="blackjack-result-frame">
            <div className="item-text-font">{seat.seat}</div>
            {seat.hands.map(v => {
                return <HandCell key={v.key} hand={v} />;
            })}
        </div>
    );
};
type HandProp = {
    hand: BJHand;
};
const getIcon = (type: IconEnum) => {
    switch (type) {
        case IconEnum.BJ:
            return <IcoBJ />;
        case IconEnum.Bush:
            return <IcoBJBust />;
        case IconEnum.Lose:
            return <IcoBJLose />;
        case IconEnum.Push:
            return <IcoBJPush />;
        case IconEnum.Win:
            return <IcoBJWin />;
        case IconEnum.Surrender:
            return <Surrender />;
    }
    return <></>;
};
const HandCell = (props: HandProp) => {
    const { hand } = props;
    const iconSvg = useMemo(() => getIcon(hand.icon), [hand.icon]);
    return (
        <div className="item-text-font white blackjack-card-frame seat">
            {hand.name}{' '}
            <div className="seat-cardlist-container">
                <BetRecordScroller gameType={GameType.Blackjack}>
                    <BetRecordPokerList
                        decks={[hand.cards]}
                        gameType={GameType.Blackjack}
                    />
                </BetRecordScroller>
            </div>
            {hand.point}
            <div className="blackjack-result-icon">{iconSvg}</div>
        </div>
    );
};

type SeatSvgProp = {
    color: Array<number>;
};
const SeatSvg = ({ color }: SeatSvgProp) => {
    return (
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 404 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="100"
                y="1.297"
                width="21"
                height="21"
                rx="10.5"
                fill="#333"
            />
            <rect
                x="130.5"
                y="10.297"
                width="21"
                height="21"
                rx="10.5"
                fill="#333"
            />
            <rect
                x="161"
                y="14.797"
                width="21"
                height="21"
                rx="10.5"
                fill="#333"
            />
            <rect
                x="191.5"
                y="14.797"
                width="21"
                height="21"
                rx="10.5"
                fill="#333"
            />
            <path
                d="M232.5 14.797c5.799 0 10.5 4.701 10.5 10.5s-4.701 10.5-10.5 10.5-10.5-4.7-10.5-10.5 4.701-10.5 10.5-10.5"
                fill="#333"
            />
            <rect
                x="252.5"
                y="10.297"
                width="21"
                height="21"
                rx="10.5"
                fill="#333"
            />
            <path
                d="M293.5 1.297c5.799 0 10.5 4.701 10.5 10.5s-4.701 10.5-10.5 10.5-10.5-4.7-10.5-10.5 4.701-10.5 10.5-10.5"
                fill="#333"
            />
            <path
                d="M110.498 7.871q.744 0 1.356.228.624.228.996.684.384.456.384 1.152 0 .516-.204.912-.204.384-.552.672-.336.288-.78.504.456.24.864.564.42.312.672.744.264.42.264.996 0 .708-.384 1.236-.372.528-1.056.816-.672.288-1.56.288-.96 0-1.632-.276t-1.032-.792q-.348-.516-.348-1.224 0-.588.216-1.02a2.5 2.5 0 0 1 .6-.744 3.6 3.6 0 0 1 .828-.54 4.3 4.3 0 0 1-.696-.54 2.7 2.7 0 0 1-.504-.696 2.2 2.2 0 0 1-.18-.912q0-.684.384-1.14a2.33 2.33 0 0 1 1.008-.684 4 4 0 0 1 1.356-.228m-1.332 6.396a1 1 0 0 0 .324.768q.336.3.984.3.456 0 .744-.132a.94.94 0 0 0 .444-.372 1 1 0 0 0 .156-.552.88.88 0 0 0-.192-.564 2 2 0 0 0-.48-.444 5 5 0 0 0-.576-.372l-.156-.084q-.372.168-.66.384a1.7 1.7 0 0 0-.432.48 1.1 1.1 0 0 0-.156.588m1.32-5.076q-.444 0-.744.228-.288.228-.288.648 0 .288.144.516.144.216.384.384.24.156.516.3.276-.132.504-.288.24-.156.384-.372a1 1 0 0 0 .144-.54q0-.42-.3-.648-.288-.228-.744-.228"
                fill={color[6] ? '#fff' : '#999'}
            />
            <path
                d="m138.898 25.547 3.216-7.044h-4.224V16.98h6.144v1.14l-3.24 7.428z"
                fill={color[5] ? '#fff' : '#999'}
            />
            <path
                d="M168.486 26.411q0-.744.108-1.464t.372-1.356q.276-.648.756-1.14.492-.504 1.236-.78.756-.288 1.824-.288.252 0 .588.024.336.012.564.06v1.452a3 3 0 0 0-.504-.084 4 4 0 0 0-.528-.036q-.804 0-1.332.192a1.9 1.9 0 0 0-.816.552q-.3.36-.444.852-.132.48-.156 1.08h.072q.168-.288.408-.504.252-.228.612-.36t.84-.132q.756 0 1.296.324.552.312.852.924.3.6.3 1.464 0 .924-.36 1.596a2.4 2.4 0 0 1-1.008 1.032q-.648.348-1.548.348-.66 0-1.236-.228a2.8 2.8 0 0 1-.996-.684q-.42-.468-.66-1.176t-.24-1.668m3.096 2.304q.528 0 .864-.36.336-.372.336-1.14 0-.624-.288-.984t-.876-.36q-.396 0-.696.18-.3.168-.468.444a1.07 1.07 0 0 0-.168.564q0 .3.084.6.084.288.24.528.168.24.408.384t.564.144"
                fill={color[4] ? '#fff' : '#999'}
            />
            <path
                d="M202.166 24.575q.78 0 1.392.3t.96.876q.36.576.36 1.428 0 .924-.384 1.596t-1.152 1.032q-.756.36-1.884.36-.672 0-1.272-.12a4 4 0 0 1-1.032-.348v-1.56a5.5 5.5 0 0 0 1.068.396q.624.156 1.176.156.54 0 .9-.144a1.13 1.13 0 0 0 .564-.444q.192-.3.192-.768 0-.636-.42-.96-.42-.336-1.296-.336-.336 0-.696.072-.36.06-.6.12l-.72-.384.324-4.368h4.644v1.536h-3.06l-.156 1.68q.204-.036.432-.072.24-.048.66-.048"
                fill={color[3] ? '#fff' : '#999'}
            />
            <path
                d="M235.198 23.4q0 .887-.54 1.415a2.73 2.73 0 0 1-1.308.72v.036q1.02.132 1.56.636t.54 1.344q0 .744-.372 1.344-.36.588-1.128.936-.756.336-1.956.336-1.392 0-2.472-.468v-1.536a5.4 5.4 0 0 0 2.28.564q.972 0 1.356-.336.396-.336.396-.948 0-.348-.18-.6t-.636-.372q-.444-.132-1.248-.132h-.648v-1.392h.66q.792 0 1.2-.144.42-.156.564-.408.156-.264.156-.6 0-.456-.288-.708-.276-.264-.936-.264a2.5 2.5 0 0 0-1.068.216 7 7 0 0 0-.756.408l-.84-1.248q.504-.36 1.176-.6.684-.24 1.62-.24 1.32 0 2.088.54.78.528.78 1.5"
                fill={color[2] ? '#fff' : '#999'}
            />
            <path
                d="M266.034 25.547h-5.988v-1.26l2.148-2.172q.648-.672 1.044-1.116.396-.456.576-.816.18-.372.18-.792 0-.516-.288-.768-.276-.252-.756-.252-.492 0-.96.228t-.984.648l-.984-1.164q.36-.312.78-.588.42-.288.96-.456.552-.18 1.32-.18.84 0 1.44.312.612.3.936.828.336.516.336 1.176 0 .708-.288 1.296-.276.588-.816 1.164-.528.576-1.284 1.272l-1.104 1.032v.084h3.732z"
                fill={color[1] ? '#fff' : '#999'}
            />
            <path
                d="M295.022 16.547h-1.812v-4.956a36 36 0 0 1 .048-1.704 4 4 0 0 1-.264.264q-.192.18-.36.324l-.984.792-.876-1.092 2.76-2.196h1.488z"
                fill={color[0] ? '#fff' : '#999'}
            />
            {color[6] && (
                <rect
                    x="100"
                    y="1.297"
                    width="21"
                    height="21"
                    rx="10.5"
                    stroke="#BF9A5D"
                    strokeWidth="1.5"
                />
            )}
            {color[5] && (
                <rect
                    x="130.5"
                    y="10.297"
                    width="21"
                    height="21"
                    rx="10.5"
                    stroke="#BF9A5D"
                    strokeWidth="1.5"
                />
            )}
            {color[4] && (
                <rect
                    x="161"
                    y="14.797"
                    width="21"
                    height="21"
                    rx="10.5"
                    stroke="#BF9A5D"
                    strokeWidth="1.5"
                />
            )}
            {color[3] && (
                <rect
                    x="191.5"
                    y="14.797"
                    width="21"
                    height="21"
                    rx="10.5"
                    stroke="#BF9A5D"
                    strokeWidth="1.5"
                />
            )}
            {color[2] && (
                <path
                    d="M232.5 14.797c5.799 0 10.5 4.701 10.5 10.5s-4.701 10.5-10.5 10.5-10.5-4.7-10.5-10.5 4.701-10.5 10.5-10.5Z"
                    stroke="#BF9A5D"
                    strokeWidth="1.5"
                />
            )}
            {color[1] && (
                <rect
                    x="252.5"
                    y="10.297"
                    width="21"
                    height="21"
                    rx="10.5"
                    stroke="#BF9A5D"
                    strokeWidth="1.5"
                />
            )}
            {color[0] && (
                <path
                    d="M293.5 1.297c5.799 0 10.5 4.701 10.5 10.5s-4.701 10.5-10.5 10.5-10.5-4.7-10.5-10.5 4.701-10.5 10.5-10.5Z"
                    stroke="#BF9A5D"
                    strokeWidth="1.5"
                />
            )}
        </svg>
    );
};
